import React, { MutableRefObject, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import s from './infinityList.module.scss';
import { InfiniteData } from 'react-query';
import InfiniteScroll from 'react-infinite-scroll-component';
import { LogoLoader } from '@components/UIKit/LogoLoader/LogoLoader';
import { EmptyList } from '@components/UIKit/EmptyList/EmptyList';
import { InfiniteListType } from '@specs/specs';
interface RenderConfig<T> {
  every: number;
  component: (props: T[]) => JSX.Element;
  itemsPerRender: number;
}
interface InfinityListProps<T> {
  data: InfiniteData<InfiniteListType<T>> | undefined;
  children: (props: T, index: number, pageIndex: number) => React.ReactNode;
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown> | unknown;
  enabledRef: MutableRefObject<boolean> | undefined;
  status: 'error' | 'loading' | 'success';
  EmptyComponent?: () => JSX.Element | null;
  listClassName?: string;
  withFooter?: boolean;
  staticItemBeforeList?: JSX.Element;
  staticItemAfterList?: JSX.Element;
  renderConfig?: RenderConfig<T>[];
}
export const InfinityList = <T extends Record<'id', number>,>({
  data,
  enabledRef,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  listClassName,
  status,
  EmptyComponent,
  withFooter = true,
  children,
  staticItemBeforeList,
  staticItemAfterList,
  renderConfig
}: InfinityListProps<T>) => {
  const {
    ref,
    inView
  } = useInView({
    threshold: 0,
    triggerOnce: true
  });
  const dataLength = data ? data.pages.flatMap(({
    items
  }) => items).length : 0;
  useEffect(() => {
    if (!inView || typeof enabledRef === 'undefined') return;
    enabledRef.current = true;
    hasNextPage !== false && dataLength === 0 && fetchNextPage();
  }, [inView, dataLength]);
  useEffect(() => {
    if (document.body.scrollHeight <= document.body.offsetHeight + (withFooter ? 600 : 0) && hasNextPage) {
      fetchNextPage();
    }
  }, [data]);
  if (status === 'success' && !dataLength || status === 'error') {
    return EmptyComponent ? <EmptyComponent /> : <EmptyList />;
  }
  const renderItems = (items: T[], pageIndex: number) => {
    const renderedElements: JSX.Element[] = [];
    if (!renderConfig) {
      return items.map((item, localIndex) => <React.Fragment key={item.id}>
                    {children(item, localIndex, pageIndex)}
                </React.Fragment>);
    }
    let loopIndex = 0;
    while (loopIndex < items.length) {
      const config = renderConfig.find(cfg => loopIndex % cfg.every === 0);
      if (config) {
        const itemsToRender = items.slice(loopIndex, loopIndex + config.itemsPerRender);
        renderedElements.push(<React.Fragment key={`config-${loopIndex}`}>
                        {config.component(itemsToRender)}
                    </React.Fragment>);
        loopIndex += config.itemsPerRender;
      } else {
        renderedElements.push(<React.Fragment key={`default-${loopIndex}`}>
                        {children(items[loopIndex], loopIndex, pageIndex)}
                    </React.Fragment>);
        loopIndex++;
      }
    }
    return renderedElements;
  };
  return <div className={s.infinityListWrapper} ref={ref} data-sentry-component="InfinityList" data-sentry-source-file="InfinityList.tsx">
            {status === 'loading' && <LogoLoader />}
            {status === 'success' && data && <>
                    <InfiniteScroll dataLength={dataLength} next={fetchNextPage} hasMore={!!hasNextPage} loader={<></>} scrollThreshold={0.65} style={{
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column'
      }}>
                        <div className={listClassName}>
                            {typeof staticItemBeforeList !== 'undefined' && staticItemBeforeList}
                            {data.pages.map((group, i) => renderItems(group.items, i))}
                            {typeof staticItemAfterList !== 'undefined' && staticItemAfterList}
                        </div>
                    </InfiniteScroll>
                    {isFetchingNextPage && <LogoLoader />}
                </>}
        </div>;
};